import { useState, useEffect } from 'react';
import { Mail, Server, Key, Send } from 'lucide-react';
import { WorkspaceSettings } from '../../types';
import { emailService } from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { updateWorkspaceEmailSettings } from '../../services/firestore';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface EmailSettingsProps {
  settings: WorkspaceSettings;
  onUpdate: (settings: Partial<WorkspaceSettings>) => Promise<void>;
  onTestEmail: () => Promise<void>;
}

export function EmailSettings({ settings }: EmailSettingsProps) {
  const { currentWorkspace } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [testEmailError, setTestEmailError] = useState('');
  const [saved, setSaved] = useState(false);

  const [formData, setFormData] = useState({
    provider: settings?.emailProvider || 'resend',
    resendApiKey: settings?.resendApiKey || '',
    fromEmail: settings?.fromEmail || '',
    fromName: settings?.fromName || '',
    smtpHost: settings?.smtpHost || '',
    smtpPort: settings?.smtpPort || 587,
    smtpUsername: settings?.smtpUsername || '',
    smtpPassword: settings?.smtpPassword || '',
    smtpSecure: settings?.smtpSecure ?? false
  });

  // Add this useEffect to sync formData with settings prop
  useEffect(() => {
    setFormData({
      provider: settings?.emailProvider || 'resend',
      resendApiKey: settings?.resendApiKey || '',
      fromEmail: settings?.fromEmail || '',
      fromName: settings?.fromName || '',
      smtpHost: settings?.smtpHost || '',
      smtpPort: settings?.smtpPort || 587,
      smtpUsername: settings?.smtpUsername || '',
      smtpPassword: settings?.smtpPassword || '',
      smtpSecure: settings?.smtpSecure ?? false
    });
  }, [settings]);

  useEffect(() => {
    // Fetch latest email settings from Firestore on mount or workspace change
    const fetchSettings = async () => {
      if (!currentWorkspace?.id) return;
      try {
        const wsDoc = await getDoc(doc(db, 'workspaces', currentWorkspace.id));
        const wsData = wsDoc.data();
        if (wsData?.settings) {
          setFormData(prev => ({
            ...prev,
            provider: wsData.settings.emailProvider || 'resend',
            resendApiKey: wsData.settings.resendApiKey || '',
            fromEmail: wsData.settings.fromEmail || '',
            fromName: wsData.settings.fromName || '',
            smtpHost: wsData.settings.smtpHost || '',
            smtpPort: wsData.settings.smtpPort || 587,
            smtpUsername: wsData.settings.smtpUsername || '',
            smtpPassword: wsData.settings.smtpPassword || '',
            smtpSecure: wsData.settings.smtpSecure ?? false
          }));
        }
      } catch (err) {
        toast.error('Failed to fetch email settings.');
      }
    };
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    setSaved(false); // Reset saved state on change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSaved(false);

    if (!formData.fromEmail || !formData.fromName) {
      alert('Please provide your business info (From Email and From Name) before saving.');
      setIsLoading(false);
      return;
    }

    try {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      await updateWorkspaceEmailSettings(currentWorkspace.id, {
        emailProvider: formData.provider as 'resend' | 'smtp',
        resendApiKey: formData.resendApiKey,
        fromEmail: formData.fromEmail,
        fromName: formData.fromName,
        smtpHost: formData.smtpHost,
        smtpPort: Number(formData.smtpPort),
        smtpUsername: formData.smtpUsername,
        smtpPassword: formData.smtpPassword,
        smtpSecure: Boolean(formData.smtpSecure)
      });
      setSaved(true);
      toast.success('Email settings updated in database!');
    } catch (error) {
      console.error('Error updating email settings:', error);
      toast.error('Failed to update email settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestEmailStatus('loading');
    setTestEmailError('');
    setSaved(false);

    try {
      // Use only the minimal required fields for test email
      const minimalWorkspace = {
        id: currentWorkspace?.id || 'test',
        settings: {
          emailProvider: formData.provider,
          fromEmail: formData.fromEmail,
          fromName: formData.fromName,
          resendApiKey: formData.resendApiKey,
        }
      };
      // @ts-ignore: minimalWorkspace is sufficient for test
      const success = await emailService.testEmailConfiguration(minimalWorkspace);
      if (success) {
        setTestEmailStatus('success');
        setSaved(true);
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        setTestEmailStatus('error');
        setTestEmailError('Failed to send test email. Please check your configuration and try again.');
        toast.error('Failed to send test email. Please check your configuration and try again.');
      }
    } catch (error) {
      setTestEmailStatus('error');
      setTestEmailError(error instanceof Error ? error.message : 'Failed to send test email');
      toast.error(error instanceof Error ? error.message : 'Failed to send test email');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900">Email Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure how emails are sent from your workspace.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email Provider
            </label>
            <select
              name="provider"
              value={formData.provider}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              <option value="resend">Resend</option>
              <option value="smtp">SMTP</option>
            </select>
          </div>

          {formData.provider === 'resend' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Resend API Key
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="resendApiKey"
                    value={formData.resendApiKey}
                    onChange={handleChange}
                    placeholder="re_..."
                    className="block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Get your API key from{' '}
                  <a 
                    href="https://resend.com/api-keys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-500 underline"
                  >
                    Resend Dashboard
                  </a>
                </p>
                {saved && formData.resendApiKey && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600">
                      ✓ API key configured. Make sure your domain is verified in Resend.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  SMTP Host
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Server className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="smtpHost"
                    value={formData.smtpHost}
                    onChange={handleChange}
                    placeholder="smtp.example.com"
                    className="block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    name="smtpPort"
                    value={formData.smtpPort}
                    onChange={handleChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Use SSL/TLS
                  </label>
                  <input
                    type="checkbox"
                    name="smtpSecure"
                    checked={formData.smtpSecure}
                    onChange={handleChange}
                    className="mt-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  SMTP Username
                </label>
                <input
                  type="text"
                  name="smtpUsername"
                  value={formData.smtpUsername}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  SMTP Password
                </label>
                <input
                  type="password"
                  name="smtpPassword"
                  value={formData.smtpPassword}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              From Email
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="email"
                name="fromEmail"
                value={formData.fromEmail}
                onChange={handleChange}
                placeholder="noreply@yourdomain.com"
                className="block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              From Name
            </label>
            <input
              type="text"
              name="fromName"
              value={formData.fromName}
              onChange={handleChange}
              placeholder="Your Company Name"
              className="block w-full px-3 py-2 sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="flex gap-4 mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>

            <button
              type="button"
              onClick={handleTestEmail}
              disabled={testEmailStatus === 'loading'}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Send className="h-4 w-4 mr-2" />
              {testEmailStatus === 'loading' ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>

          {testEmailStatus === 'success' && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">Test email sent successfully!</p>
            </div>
          )}

          {testEmailStatus === 'error' && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{testEmailError || 'Failed to send test email'}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}